"use client";

import { graphql, readFragment, useMutation, useQuery } from "@opensea/graphql";
import { TimelineSkeleton } from "@opensea/ui-kit";
import { useCallback } from "react";
import { ActionTimeline, ActionTimelineError } from "../../../../../components/ActionTimeline";
import { ActionTimelineFragment } from "../../../../../components/ActionTimeline/ActionTimeline.fragment";
import { useAddress } from "../../../../../polychain/hooks";
import { useTrackError } from "../../../../../providers/TrackErrorProvider";
import { useActionErrors } from "../../../../hooks/useErrors";
import { useSetBlockchainActionErrors } from "../../../../hooks/useSetBlockchainActionErrors";
import { useSetBlockchainActionErrorsFragment } from "../../../../hooks/useSetBlockchainActionErrors.fragment";
import { readOrderCreationError } from "../../../../utils/readOrderCreationError";
import { readOrderCreationErrorFragment } from "../../../../utils/readOrderCreationError.fragment";
import { seaportOrder } from "../../../../utils/seaportOrder";
import { seaportOrderFragment } from "../../../../utils/seaportOrder.fragment";
import { CreateCollectionOfferTimeline_collectionFragment } from "./CreateCollectionOfferTimeline.fragment";
export const CreateCollectionOfferTimeline = ({
  collection: collectionKey,
  offer,
  onError,
  onSuccess,
  onBack
}) => {
  const collection = readFragment(CreateCollectionOfferTimeline_collectionFragment, collectionKey);
  const address = useAddress();
  let traitCriteria;
  if (offer.criteria.traits && offer.criteria.traits.length > 0) {
    traitCriteria = offer.criteria.traits;
  } else if (offer.criteria.trait) {
    traitCriteria = [offer.criteria.trait];
  }
  const [{
    data,
    error
  }, refetch] = useQuery({
    query: CreateCollectionOfferTimelineQuery,
    variables: {
      criteria: {
        slug: offer.criteria.slug,
        contractIdentifier: {
          address: offer.criteria.contractAddress,
          chain: offer.criteria.chain
        },
        traitIdentifier: traitCriteria
      },
      quantity: offer.quantity,
      address: address,
      pricePerItem: {
        unit: offer.pricePerItem.unit.toString(),
        contractAddress: offer.pricePerItem.contractAddress
      },
      startTime: offer.startTime.toISOString(),
      endTime: offer.endTime.toISOString()
    },
    pause: !address
  });
  const trackError = useTrackError();
  const [_, mutate] = useMutation(CreateCollectionOfferTimelineMutation);
  const actions = data?.createCollectionOfferActions.actions;
  const onDone = useCallback(async response => {
    if (response.action !== "SIGNATURE") {
      throw new Error("No signature response found from scheduled actions");
    }
    const order = data?.createCollectionOfferActions.actions.flatMap(a => a.__typename === "CreateCollectionOfferAction" ? [a] : [])[0].order;
    if (order) {
      const mutation = await mutate({
        order: seaportOrder(order),
        chain: offer.criteria.chain,
        signature: response.signature,
        criteria: {
          slug: collection.slug,
          contractIdentifier: {
            address: collection.address,
            chain: collection.chain.identifier
          },
          traitIdentifier: traitCriteria
        }
      });
      const dataError = readOrderCreationError(mutation.data?.createCollectionOfferV2);
      if (mutation.error) {
        trackError(mutation.error, {
          extra: {
            offer
          },
          tags: {
            flow: "create-collection-offer",
            address: address || "",
            collection: collection.slug,
            chain: collection.chain.identifier
          }
        });
        onError?.(new Error(mutation.error.message));
      } else if (dataError) {
        onError?.(new Error(dataError.message));
      } else {
        onSuccess?.();
      }
    }
  }, [address, collection, data, mutate, offer, onError, onSuccess, trackError]);
  useSetBlockchainActionErrors(data?.createCollectionOfferActions.errors);
  const actionError = useActionErrors();
  if (!actions) {
    return <TimelineSkeleton />;
  }
  if (error) {
    return <ActionTimelineError message="Something went wrong" onBack={onBack} onRetry={() => refetch({
      requestPolicy: "network-only"
    })} />;
  }
  if (actionError) {
    return <ActionTimelineError message={actionError} onBack={onBack} onRetry={() => refetch({
      requestPolicy: "network-only"
    })} />;
  }
  return <ActionTimeline actions={data.createCollectionOfferActions.actions} onAllActionsCompleted={onDone} onBack={onBack} />;
};
const CreateCollectionOfferTimelineQuery = graphql(`
    query CreateCollectionOfferTimelineQuery(
      $criteria: CollectionOfferCriteriaInput!
      $quantity: BigInteger!
      $address: Address!
      $pricePerItem: PriceInput!
      $startTime: DateTime!
      $endTime: DateTime!
    ) {
      createCollectionOfferActions(
        criteria: $criteria
        quantity: $quantity
        address: $address
        pricePerItem: $pricePerItem
        startTime: $startTime
        endTime: $endTime
      ) {
        actions {
          __typename
          ... on CreateCollectionOfferAction {
            order {
              ...seaportOrder
            }
          }
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `, [ActionTimelineFragment, seaportOrderFragment, useSetBlockchainActionErrorsFragment]);
const CreateCollectionOfferTimelineMutation = graphql(`
    mutation CreateCollectionOfferTimelineMutation(
      $chain: ChainIdentifier!
      $order: SeaportOrderComponentsInput!
      $signature: String!
      $criteria: CollectionOfferCriteriaInput!
    ) {
      createCollectionOfferV2(
        chain: $chain
        order: $order
        signature: $signature
        criteria: $criteria
      ) {
        ...readOrderCreationError
      }
    }
  `, [readOrderCreationErrorFragment]);